import React, { useState, useCallback } from 'react';
import { Table, Button } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { usePdaHomeConfigList } from './hooks/usePdaHomeConfigList';
import { useWarehouses } from './hooks/useWarehouses';
import PdaHomeConfigDrawer from './components/PdaHomeConfigDrawer';
import ParcelFilterStyle from '../../delivery/components/ParcelFilterStyle';
import { getSmartLabelClassName } from '../../../utils/labelWidthUtils';
import TablePaginationBar from '../../../components/TablePaginationBar';

const getRoleOptions = (t: any) => [
  { value: 'HUB_OPERATOR', label: t('pda.homeConfig.roles.HUB_OPERATOR') },
  { value: 'HUB_MANAGER', label: t('pda.homeConfig.roles.HUB_MANAGER') },
  { value: 'PICKUP_STAFF', label: t('pda.homeConfig.roles.PICKUP_DRIVER') },
  { value: 'SORTING_STAFF', label: t('pda.homeConfig.roles.SORTING_STAFF') },
  { value: 'WAREHOUSE_OPERATOR', label: t('pda.homeConfig.roles.WAREHOUSE_OPERATOR') },
  { value: 'WAREHOUSE_MANAGER', label: t('pda.homeConfig.roles.WAREHOUSE_MANAGER') },
  { value: 'RETURNS_STAFF', label: t('pda.homeConfig.roles.RETURNS_STAFF') },
  { value: 'SORTER_OPERATOR', label: t('pda.homeConfig.roles.SORTER_OPERATOR') },
];

const PdaHomeConfigPage: React.FC = () => {
  const { t } = useTranslation();
  const [selectedWarehouseId, setSelectedWarehouseId] = useState<number | null>(null);
  const [selectedRole, setSelectedRole] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [editingConfig, setEditingConfig] = useState<{ warehouseId: number; role: string } | null>(null);

  const ROLE_OPTIONS = getRoleOptions(t);
  const { warehouses, loading: warehousesLoading } = useWarehouses();
  const { configs, totalElements, loading, refetch } = usePdaHomeConfigList(
    selectedWarehouseId,
    selectedRole,
    currentPage,
    pageSize,
    t
  );

  const handleAddNew = useCallback(() => {
    setEditingConfig(null);
    setDrawerVisible(true);
  }, []);

  const handleEdit = useCallback((warehouseId: number, role: string) => {
    setEditingConfig({ warehouseId, role });
    setDrawerVisible(true);
  }, []);

  const handleDrawerClose = useCallback(() => {
    setDrawerVisible(false);
    setEditingConfig(null);
  }, []);

  const handleSaveSuccess = useCallback(() => {
    setDrawerVisible(false);
    setEditingConfig(null);
    refetch();
  }, [refetch]);

  const handleWarehouseChange = useCallback((value: number | null) => {
    setSelectedWarehouseId(value);
    setCurrentPage(1);
  }, []);

  const handleRoleChange = useCallback((value: string | null) => {
    setSelectedRole(value);
    setCurrentPage(1);
  }, []);

  const columns = [
    {
      title: t('pda.homeConfig.warehouseName'),
      dataIndex: 'warehouseName',
      key: 'warehouseName',
      width: 200,
      render: (warehouseName: string, record: any) => {
        return record.warehouseId === 0 ? 'Common' : warehouseName;
      },
    },
    {
      title: t('pda.homeConfig.role'),
      dataIndex: 'accountRole',
      key: 'accountRole',
      width: 180,
      render: (role: string) => {
        const roleOption = ROLE_OPTIONS.find(r => r.value === role);
        return roleOption ? roleOption.label : role;
      },
    },
    {
      title: t('pda.homeConfig.actions'),
      key: 'action',
      width: 100,
      fixed: 'right' as const,
      render: (_: any, record: any) => (
        <Button
          type="link"
          onClick={() => handleEdit(record.warehouseId, record.accountRole)}
        >
          {t('pda.homeConfig.edit')}
        </Button>
      ),
    },
  ];

  const warehouseOptions = [
    { value: '', label: t('pda.homeConfig.allWarehouses') },
    ...warehouses.map((wh: any) => ({
      value: String(wh.id),
      label: wh.name,
    })),
  ];

  const roleOptions = [
    { value: '', label: t('pda.homeConfig.allRoles') },
    ...ROLE_OPTIONS.map(opt => ({
      value: opt.value,
      label: opt.label,
    })),
  ];

  const col1Labels = [t('pda.homeConfig.warehouse')];
  const col2Labels = [t('pda.homeConfig.role')];

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-3">
      {/* 筛选区域 */}
      <div className="mb-2 pl-0 py-2">
        <div className="grid grid-cols-3 gap-x-10 gap-y-4 items-start">
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('pda.homeConfig.warehouse')}
              options={warehouseOptions}
              value={selectedWarehouseId !== null ? String(selectedWarehouseId) : ''}
              onChange={(value: string | undefined) => {
                handleWarehouseChange(value && value !== '' ? Number(value) : null);
              }}
              className="w-full"
              spanClassName={getSmartLabelClassName(col1Labels)}
              loading={warehousesLoading}
            />
          </div>
          <div className="min-w-0">
            <ParcelFilterStyle
              label={t('pda.homeConfig.role')}
              options={roleOptions}
              value={selectedRole || ''}
              onChange={(value: string | undefined) => {
                handleRoleChange(value && value !== '' ? value : null);
              }}
              className="w-full"
              spanClassName={getSmartLabelClassName(col2Labels)}
            />
          </div>
          <div className="min-w-0 flex items-end">
            <Button type="primary" icon={<PlusOutlined />} onClick={handleAddNew}>
              {t('pda.homeConfig.newConfig')}
            </Button>
          </div>
        </div>
      </div>

      <Table
        dataSource={configs}
        columns={columns}
        loading={loading}
        pagination={false}
        rowKey={(record) => record.groupId}
        scroll={{ x: 'max-content' }}
        size="middle"
      />

      <TablePaginationBar
        total={totalElements || 0}
        current={currentPage}
        pageSize={pageSize}
        onChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
      />

      <PdaHomeConfigDrawer
        visible={drawerVisible}
        editingConfig={editingConfig}
        onClose={handleDrawerClose}
        onSuccess={handleSaveSuccess}
      />
    </div>
  );
};

export default PdaHomeConfigPage;
